import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { MatDialog } from '@angular/material/dialog';
import * as MobilityManagerActions from './mobility-manager.actions';
import { catchError, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import {
  OrganizationPaymentAccount,
  OrganizationPaymentAccountService,
  PaymentAccountErrorConstants,
  PaymentAccountService,
  PaymentMethod,
  PaymentUtil,
  PaymentAccount
} from '@uzurv/clara';
import { MobilityManagerPaymentAccountDialog } from '../../components/dialogs/payment-account-dialog/mobility-manager/mobility-manager-payment-account-dialog.dialog';
import { AlertDialogComponent } from '../../components/dialogs/alert-dialog/alert-dialog.component';
import { EMPTY, of } from 'rxjs';
import { isNullOrUndefined, PaymentMethodConstants, PermissionsConstants } from '@uzurv/core';
import { ConfirmationDialogComponent } from '../../components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { selectMobilityManagerState } from './mobility-manager.selectors';
import { AuthDataService } from '@uzurv/http';
import { Router } from '@angular/router';

@Injectable()
export class MobilityManagerEffects {
  constructor(
    private actions$: Actions,
    private store$: Store,
    private dialog: MatDialog,
    private organizationPaymentAccountService: OrganizationPaymentAccountService,
    private paymentAccountService: PaymentAccountService,
    private authDataService: AuthDataService,
    private router: Router
  ) {}

  getOrganizationPaymentAccounts$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        MobilityManagerActions.GetOrganizationPaymentAccounts,
        MobilityManagerActions.Start,
        MobilityManagerActions.CreateOrganizationPaymentAccountSuccess,
        MobilityManagerActions.EditOrganizationPaymentAccountSuccess,
        MobilityManagerActions.DeleteOrganizationPaymentAccountSuccess,
        MobilityManagerActions.SetDefaultOrganizationPaymentAccountSuccess
      ),
      switchMap(action =>
        this.organizationPaymentAccountService.listOrganizationPaymentAccounts().pipe(
          map(organizationPaymentAccounts => {
            return MobilityManagerActions.GetOrganizationPaymentAccountSuccess({ organizationPaymentAccounts });
          }),
          catchError(error => of(MobilityManagerActions.GetOrganizationPaymentAccountFail({ error })))
        )
      )
    );
  });

  getCardTypes$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(MobilityManagerActions.GetCardTypes, MobilityManagerActions.Start),
      switchMap(() =>
        this.paymentAccountService.getCardTypes().pipe(
          map(cardTypes => MobilityManagerActions.GetCardTypesSuccess({ cardTypes })),
          catchError(error => of(MobilityManagerActions.GetCardTypesFail({ error })))
        )
      )
    );
  });

  openAddOrganizationPaymentAccountDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MobilityManagerActions.OpenAddOrganizationPaymentAccountDialog),
        map(() => {
          this.dialog.open(MobilityManagerPaymentAccountDialog, {
            autoFocus: 'dialog',
            restoreFocus: true,
            width: '35em',
            maxWidth: '35em',
            minWidth: '0',
            hasBackdrop: true,
            data: {
              organizationPaymentAccount: Object.assign(new OrganizationPaymentAccount(), { paymentAccount: new PaymentAccount() }),
              isEditMode: false
            }
          });
        })
      ),
    { dispatch: false }
  );

  createOrganizationPaymentAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(MobilityManagerActions.CreateOrganizationPaymentAccount),
      filter(action => !isNullOrUndefined(action.dialogRef) && !isNullOrUndefined(action.organizationPaymentAccount?.paymentAccount)),
      withLatestFrom(this.store$.select(selectMobilityManagerState)),
      switchMap(([action, state]) => {
        action.organizationPaymentAccount.paymentAccount.personId = this.authDataService.currentUser.claraPersonId;

        // Credit Card Validation
        try {
          PaymentUtil.validateCreditCard(
            action.organizationPaymentAccount.paymentAccount.cardNumber,
            action.organizationPaymentAccount.paymentAccount.cardExpiration,
            state.cardTypes
          );
        } catch (error) {
          return of(MobilityManagerActions.CreateOrganizationPaymentAccountFail({ error }));
        }

        // Expiration Date
        action.organizationPaymentAccount.paymentAccount.cardExpiration = PaymentUtil.formatCreditCardExpirationDate(
          action.organizationPaymentAccount.paymentAccount.cardExpiration
        );

        // Payment Method
        const cardPaymentMethod = new PaymentMethod();
        cardPaymentMethod.code = PaymentMethodConstants.CARD;
        action.organizationPaymentAccount.paymentAccount.paymentMethod = cardPaymentMethod;

        // Card Type
        const cardTypeCode: string = PaymentUtil.getCardTypeCodeFromCreditCard(action.organizationPaymentAccount.paymentAccount.cardNumber);
        action.organizationPaymentAccount.paymentAccount.cardType = state.cardTypes.find(ct => ct.code === cardTypeCode);

        // Save
        return this.organizationPaymentAccountService.createOrganizationPaymentAccount(action.organizationPaymentAccount).pipe(
          map(organizationPaymentAccount => {
            action.dialogRef.close();
            this.dialog.open(AlertDialogComponent, {
              autoFocus: 'dialog',
              restoreFocus: true,
              maxWidth: '20vw',
              data: {
                title: 'Payment Account Created',
                message: `${organizationPaymentAccount.paymentAccount.cardType.name} ending in: ${organizationPaymentAccount.paymentAccount.cardNumber} was successfully created`
              }
            });
            return MobilityManagerActions.CreateOrganizationPaymentAccountSuccess({ organizationPaymentAccount });
          }),
          catchError(error => of(MobilityManagerActions.CreateOrganizationPaymentAccountFail({ error })))
        );
      })
    );
  });

  openEditOrganizationPaymentAccountDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MobilityManagerActions.OpenEditOrganizationPaymentAccountDialog),
        map(action => {
          this.dialog.open(MobilityManagerPaymentAccountDialog, {
            autoFocus: 'dialog',
            restoreFocus: true,
            width: '35em',
            maxWidth: '35em',
            minWidth: '0',
            hasBackdrop: true,
            data: {
              organizationPaymentAccount: action.organizationPaymentAccount,
              isEditMode: true
            }
          });
        })
      ),
    { dispatch: false }
  );

  editOrganizationPaymentAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(MobilityManagerActions.EditOrganizationPaymentAccount),
      filter(action => !isNullOrUndefined(action.dialogRef) && !isNullOrUndefined(action.organizationPaymentAccount?.paymentAccount)),
      switchMap(action => {
        // Credit Card Validation
        try {
          PaymentUtil.validateCreditCardExpirationDate(action.organizationPaymentAccount.paymentAccount.cardExpiration);
        } catch (error) {
          return of(MobilityManagerActions.EditOrganizationPaymentAccountFail({ error }));
        }

        // Expiration Date
        action.organizationPaymentAccount.paymentAccount.cardExpiration = PaymentUtil.formatCreditCardExpirationDate(
          action.organizationPaymentAccount.paymentAccount.cardExpiration
        );

        return this.organizationPaymentAccountService.updateOrganizationPaymentAccount(action.organizationPaymentAccount).pipe(
          map(organizationPaymentAccount => {
            action.dialogRef.close();
            this.dialog.open(AlertDialogComponent, {
              autoFocus: 'dialog',
              restoreFocus: true,
              maxWidth: '20vw',
              data: {
                title: 'Payment Account Updated',
                message: `${organizationPaymentAccount.paymentAccount.cardType.name} ending in: ${organizationPaymentAccount.paymentAccount.cardNumber} was successfully updated`
              }
            });
            return MobilityManagerActions.EditOrganizationPaymentAccountSuccess({ organizationPaymentAccount });
          }),
          catchError(error => of(MobilityManagerActions.EditOrganizationPaymentAccountFail({ error })))
        );
      })
    );
  });

  openDeleteOrganizationPaymentAccountDialog$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(MobilityManagerActions.OpenDeleteOrganizationPaymentAccountDialog),
        map(action => {
          if (action.organizationPaymentAccount.default) {
            this.openDeleteDefaultOrganizationPaymentAccountDialog(action.organizationPaymentAccount);
          } else {
            this.openDeleteOrganizationPaymentAccountDialog(action.organizationPaymentAccount);
          }
        })
      );
    },
    { dispatch: false }
  );

  deleteOrganizationPaymentAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(MobilityManagerActions.DeleteOrganizationPaymentAccount),
      switchMap(action => {
        return this.organizationPaymentAccountService.deleteOrganizationPaymentAccount(action.organizationPaymentAccount.id).pipe(
          map(() => {
            this.dialog.open(AlertDialogComponent, {
              autoFocus: 'dialog',
              restoreFocus: true,
              maxWidth: '20vw',
              data: {
                title: 'Success',
                message: `Payment Account Successfully Deleted`
              }
            });
            return MobilityManagerActions.DeleteOrganizationPaymentAccountSuccess();
          }),
          catchError(error => of(MobilityManagerActions.DeleteOrganizationPaymentAccountFail({ error })))
        );
      })
    );
  });

  openDefaultOrganizationPaymentAccountDialog$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(MobilityManagerActions.OpenDefaultOrganizationPaymentAccountDialog),
      switchMap(action =>
        this.dialog
          .open(ConfirmationDialogComponent, {
            autoFocus: 'dialog',
            restoreFocus: true,
            data: {
              title: 'Change Default Payment Method',
              message: `Make <b>${action.organizationPaymentAccount.paymentAccount.userDescription}</b> (${action.organizationPaymentAccount.paymentAccount.cardType.name} ${action.organizationPaymentAccount.paymentAccount.cardNumber}) your default payment method?`,
              confirmText: 'Make Default'
            }
          })
          .afterClosed()
          .pipe(
            switchMap(confirmed => {
              if (!confirmed) {
                return EMPTY;
              }
              return of(
                MobilityManagerActions.SetDefaultOrganizationPaymentAccount({
                  organizationPaymentAccount: action.organizationPaymentAccount
                })
              );
            })
          )
      )
    );
  });

  openOrganizationPaymentAccountNeededDialog$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(MobilityManagerActions.OpenOrganizationPaymentAccountNeededDialog),
      switchMap(() => {
        if (this.authDataService.hasPermission({ any: [PermissionsConstants.ORGANIZATION_PAYMENT_ACCOUNT_WRITE] })) {
          return this.dialog
            .open(ConfirmationDialogComponent, {
              autoFocus: 'dialog',
              restoreFocus: true,
              data: {
                title: 'Please Provide a Payment Method',
                message: 'Before you can schedule any new rides, you must provide a valid payment method.',
                confirmText: 'Add a Payment Method',
                dismissText: 'Cancel'
              }
            })
            .afterClosed()
            .pipe(
              switchMap(confirmed => {
                if (!confirmed) {
                  return EMPTY;
                }
                return of(MobilityManagerActions.OpenAddOrganizationPaymentAccountDialog());
              })
            );
        } else {
          this.dialog.open(AlertDialogComponent, {
            autoFocus: 'dialog',
            restoreFocus: true,
            data: {
              title: 'Please Provide a Payment Method',
              message:
                'Your organization does not have a saved payment method. Contact your organization’s admin about adding one. You may schedule new rides once a valid method of payment has been saved.'
            }
          });
          return EMPTY;
        }
      })
    );
  });

  openDefaultOrganizationPaymentAccountExpiredDialog$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(MobilityManagerActions.OpenDefaultOrganizationPaymentAccountExpiredDialog),
        switchMap(action => {
          if (this.authDataService.hasPermission({ any: [PermissionsConstants.ORGANIZATION_PAYMENT_ACCOUNT_WRITE] })) {
            return this.dialog
              .open(ConfirmationDialogComponent, {
                autoFocus: 'dialog',
                restoreFocus: true,
                data: {
                  title: 'Please Provide a Valid Payment Method',
                  message: `Before you can schedule any new rides, you must provide a valid payment method. Your current default payment method,
                        <b>${action.organizationPaymentAccount.paymentAccount.userDescription}</b> (${action.organizationPaymentAccount.paymentAccount.cardType.name} ${action.organizationPaymentAccount.paymentAccount.cardNumber}), is expired.`,
                  confirmText: 'Go to Payment Methods',
                  dismissText: 'Cancel'
                }
              })
              .afterClosed()
              .pipe(
                switchMap(confirmed => {
                  if (!confirmed) {
                    return EMPTY;
                  }
                  this.router.navigate(['/payment-accounts']);
                })
              );
          } else {
            this.dialog.open(AlertDialogComponent, {
              autoFocus: 'dialog',
              restoreFocus: true,
              data: {
                title: 'A Valid Payment Method is Required',
                message:
                  'Your organization’s default payment method has expired. Contact your organization’s admin about updating it. You may schedule new rides once a valid method of payment has been saved.'
              }
            });
            return EMPTY;
          }
        })
      );
    },
    { dispatch: false }
  );

  setDefaultOrganizationPaymentAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(MobilityManagerActions.SetDefaultOrganizationPaymentAccount),
      switchMap(action => {
        action.organizationPaymentAccount.default = true;
        return this.organizationPaymentAccountService.updateOrganizationPaymentAccount(action.organizationPaymentAccount).pipe(
          map(organizationPaymentAccount => {
            this.dialog.open(AlertDialogComponent, {
              autoFocus: 'dialog',
              restoreFocus: true,
              data: {
                title: 'Payment Account Updated',
                message: `${organizationPaymentAccount.paymentAccount.cardType.name} ending in: ${organizationPaymentAccount.paymentAccount.cardNumber} was successfully updated`
              }
            });
            return MobilityManagerActions.SetDefaultOrganizationPaymentAccountSuccess();
          }),
          catchError(error => of(MobilityManagerActions.SetDefaultOrganizationPaymentAccountFail({ error })))
        );
      })
    );
  });

  error$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          MobilityManagerActions.CreateOrganizationPaymentAccountFail,
          MobilityManagerActions.EditOrganizationPaymentAccountFail,
          MobilityManagerActions.DeleteOrganizationPaymentAccountFail,
          MobilityManagerActions.SetDefaultOrganizationPaymentAccountFail
        ),
        map(action => {
          let title = 'Error';
          if (action.error.message.includes('E00042')) {
            action.error.message =
              'Payment processor only supports 10 payment accounts. If you believe this to be an error, contact your administrator';
          }
          if (action.error.message.includes('Street address 1 must not be null')) {
            action.error.message = 'Street address one must not be empty.';
          }
          if (action.error.message.includes('City must not be null')) {
            action.error.message = 'City must not be empty.';
          }
          if (action.error.message.includes('State code must not be null')) {
            action.error.message = 'State must not be empty.';
          }
          if (action.error.message.includes('Zip code must not be null')) {
            action.error.message = 'Zip code must not be empty.';
          }
          if (action.error.message.includes('There is one or more missing or invalid required fields')) {
            title = 'Verification Failed';
            action.error.message = 'One or more fields are missing or invalid';
          }
          if (PaymentAccountErrorConstants.TRANSACTION_DECLINED.test(action.error.message)) {
            title = 'Verification Failed';
            action.error.message =
              'The CVV code or billing address you entered for this card is incorrect. Please check your information and wait at least 2 minutes before trying again.';
          }
          if (PaymentAccountErrorConstants.DUPLICATE_TRANSACTION.test(action.error.message)) {
            title = 'Please Wait';
            action.error.message = 'Please wait at least 2 minutes before trying to resubmit your information.';
          }
          if (PaymentAccountErrorConstants.PAYMENT_ACCOUNT_ALREADY_ON_FILE.test(action.error.message)) {
            action.error.message = 'Payment Account Already On File.';
          }

          this.dialog.open(AlertDialogComponent, {
            autoFocus: 'dialog',
            restoreFocus: true,
            data: {
              title: title,
              message: action.error.message
            }
          });
        })
      );
    },
    { dispatch: false }
  );

  private openDeleteOrganizationPaymentAccountDialog(organizationPaymentAccount: OrganizationPaymentAccount) {
    this.dialog
      .open(ConfirmationDialogComponent, {
        autoFocus: 'dialog',
        restoreFocus: true,
        data: {
          title: 'Delete Payment Account',
          message: `Delete <b>${organizationPaymentAccount.paymentAccount.userDescription}</b> (${organizationPaymentAccount.paymentAccount.cardType.name} ${organizationPaymentAccount.paymentAccount.cardNumber}) from your saved payment methods?`,
          isDestructive: true
        }
      })
      .afterClosed()
      .subscribe(confirmed => {
        if (!confirmed) {
          return;
        }
        this.store$.dispatch(MobilityManagerActions.DeleteOrganizationPaymentAccount({ organizationPaymentAccount }));
      });
  }

  private openDeleteDefaultOrganizationPaymentAccountDialog(organizationPaymentAccount: OrganizationPaymentAccount) {
    const dialogRef = this.dialog.open(AlertDialogComponent, {
      autoFocus: 'dialog',
      restoreFocus: true,
      maxWidth: '20vw',
      data: {
        title: 'Default Payment Method Cannot Be Deleted',
        message: `<b>${organizationPaymentAccount.paymentAccount.userDescription}</b> (${organizationPaymentAccount.paymentAccount.cardType.name} ${organizationPaymentAccount.paymentAccount.cardNumber}) cannot be deleted because it is your default payment method. Please set a new default payment method if you want to delete this one.`,
        isDestructive: true,
        buttons: [
          {
            text: 'Close',
            handler: () => dialogRef.close()
          }
        ]
      }
    });
  }
}
